import { supabase } from "../lib/supabase";
import { createEnrollmentQueries } from "./enrollmentQueries";
import { analyticsService } from "./analyticsService";
import { fetchAllRows } from "../utils/reporting.js";
import { getCountableEnrollmentLessons } from "../utils/enrollmentLessons";
import { getCurrentPlatform, PLATFORMS } from "../utils/platform";
import { authService } from "./authService";
import { notificationService } from "./notificationService";
import {
  excludeAnalyticsUsers,
  excludeAnalyticsTransactions,
  getAnalyticsTransactionExclusionIds,
  getAnalyticsTestUserIds,
} from "./analyticsFilterService";
import {
  isClassroomDeliveryMode,
  isOnlineDeliveryMode,
} from "../utils/trainingDelivery";

const isTransientNetworkError = (error) =>
  /^(TypeError:\s*)?(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?)$/i.test(
    String(error?.message || error || "").trim(),
  );

const getSiteUrl = () =>
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "";

const enrollmentQueries = createEnrollmentQueries(supabase);

/**
 * Service for managing course enrollments and related statistics.
 * Centralizes logic previously scattered across courseService, userService, and UI components.
 */
export const enrollmentService = {
  /**
   * Get all enrollments for a specific user with full course details.
   * Standardizes the join query used by My Courses and Dashboard.
   */
  async getUserEnrollments(userId, platform = null, partnerId = null) {
    if (!userId) return [];

    try {
      const hasSession = await authService.ensureFreshToken();
      if (!hasSession) return [];

      // Default to the current platform when none is given — backward
      // compatible, and it keeps platform separation enforced.
      const { data, error } = await enrollmentQueries.userEnrollments({
        userId,
        platform: platform || getCurrentPlatform(),
        partnerId,
      });

      if (error) throw error;

      return (data || []).map(this.normalizeEnrollment);
    } catch (error) {
      console.error("Error in getUserEnrollments:", error);
      throw error;
    }
  },

  /**
   * Get a single enrollment by ID.
   */
  async getEnrollmentById(enrollmentId) {
    try {
      const { data, error } = await supabase
        .from("course_enrollments")
        .select(
          `
          *,
          course:courses(id, title, thumbnail_url, lessons(id))
        `,
        )
        .eq("id", enrollmentId)
        .single();

      if (error) throw error;
      return this.normalizeEnrollment(data);
    } catch (error) {
      console.error("Error in getEnrollmentById:", error);
      throw error;
    }
  },

  /**
   * Check if a user is enrolled in a specific course.
   * Course access is shared across storefronts, so this lookup intentionally
   * does not filter by the platform where the enrollment was created.
   *
   * Returns null ONLY when the check succeeded and no enrollment exists.
   * Query/network failures throw (after retrying transient errors) so
   * callers can show a retryable error instead of a false "not enrolled".
   */
  async getUserEnrollment(
    userId,
    courseId,
    { deliveryMode = "self_paced", batchEnrollmentId = null } = {},
  ) {
    if (!userId || !courseId) return null;

    const hasSession = await authService.ensureFreshToken();
    if (!hasSession) return null;

    const RETRY_DELAYS_MS = [300, 900];

    for (let attempt = 0; ; attempt++) {
      try {
        let query = supabase
          .from("course_enrollments")
          .select("*")
          .eq("course_id", courseId)
          .eq("user_id", userId)
          .eq("delivery_mode", deliveryMode)
          .neq("enrollment_status", "cancelled")
          .order("enrolled_at", { ascending: false })
          .limit(1);
        if (batchEnrollmentId) {
          query = query.eq("batch_enrollment_id", batchEnrollmentId);
        }
        const { data, error } = await query.maybeSingle();

        if (error) throw error;
        return data ? this.normalizeEnrollment(data) : null;
      } catch (error) {
        if (
          isTransientNetworkError(error) &&
          attempt < RETRY_DELAYS_MS.length
        ) {
          console.warn(
            `Transient getUserEnrollment failure (retry ${attempt + 1}):`,
            error.message,
          );
          await new Promise((res) => setTimeout(res, RETRY_DELAYS_MS[attempt]));
          continue;
        }
        console.error("Error in getUserEnrollment:", error);
        throw error;
      }
    }
  },

  /**
   * Resolve enrollment independently for self-paced, live virtual, and
   * classroom delivery. Batch checkout also creates a course_enrollments row
   * for course-material access, so self-paced ownership is read from the
   * enrollment's own delivery_mode rather than inferred from payments.
   */
  async getUserEnrollmentVariants(userId, partnerId = null) {
    if (!userId) {
      return {
        courseEnrollments: [],
        selfPacedCourseIds: [],
        virtualCourseIds: [],
        classroomCourseIds: [],
      };
    }

    const hasSession = await authService.ensureFreshToken();
    if (!hasSession) {
      return {
        courseEnrollments: [],
        selfPacedCourseIds: [],
        virtualCourseIds: [],
        classroomCourseIds: [],
      };
    }

    const [courseResult, batchResult] = await Promise.all([
      enrollmentQueries.selfPacedEnrollments(userId),
      enrollmentQueries.batchEnrollments(userId),
    ]);

    const failedResult = [courseResult, batchResult].find(
      (result) => result.error,
    );
    if (failedResult?.error) throw failedResult.error;

    const courseEnrollments = (courseResult.data || []).map((enrollment) =>
      this.normalizeEnrollment(enrollment),
    );
    const selfPacedCourseIds = courseEnrollments.map(
      (enrollment) => enrollment.course_id,
    );

    const virtualCourseIds = new Set();
    const classroomCourseIds = new Set();
    (batchResult.data || []).forEach((enrollment) => {
      const courseId = enrollment.batch?.course_id;
      const deliveryMode = enrollment.batch?.delivery_mode;
      if (
        !courseId ||
        (partnerId && enrollment.batch?.partner_id !== partnerId)
      ) {
        return;
      }
      if (isOnlineDeliveryMode(deliveryMode)) virtualCourseIds.add(courseId);
      if (isClassroomDeliveryMode(deliveryMode)) {
        classroomCourseIds.add(courseId);
      }
    });

    return {
      courseEnrollments,
      selfPacedCourseIds: [...new Set(selfPacedCourseIds)],
      virtualCourseIds: [...virtualCourseIds],
      classroomCourseIds: [...classroomCourseIds],
    };
  },

  /**
   * Enroll the signed-in user in free courses via the enroll-free-courses
   * edge function, which validates server-side that every course is really
   * priced at zero. Throws on full failure; on partial failure the thrown
   * error carries `enrolled` / `failed` course-id arrays.
   */
  async enrollFreeCourses({
    courseIds,
    accreditationTypes = {},
    partnerId = null,
  }) {
    const session = await authService.getValidSession();
    if (!session?.access_token) throw new Error("Authentication required");

    const { data, error } = await supabase.functions.invoke(
      "enroll-free-courses",
      {
        body: {
          courseIds,
          accreditationTypes,
          partnerId: partnerId || undefined,
          platform: getCurrentPlatform(),
          siteUrl: getSiteUrl(),
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      },
    );

    if (error) {
      // supabase-js reports non-2xx as a generic "non-2xx status code"
      // message; the function's JSON body carries the real reason.
      let body = null;
      try {
        body = await error?.context?.json?.();
      } catch {
        body = null;
      }
      const enrollError = new Error(
        body?.error ||
          (error?.context?.status === 401
            ? "Please sign in again to continue."
            : "Free enrollment failed. Please try again."),
      );
      enrollError.status = error?.context?.status ?? null;
      enrollError.enrolled = body?.enrolled || [];
      enrollError.failed = body?.failed || [];
      throw enrollError;
    }
    if (!data?.success) {
      const failedCount = data?.failed?.length || 0;
      const enrollError = new Error(
        data?.error ||
          (failedCount
            ? `Enrollment failed for ${failedCount} course(s). Please try again.`
            : "Free enrollment failed"),
      );
      enrollError.enrolled = data?.enrolled || [];
      enrollError.failed = data?.failed || [];
      throw enrollError;
    }
    return data;
  },

  /**
   * Enroll a user in a course.
   */
  async enrollInCourse(
    userId,
    courseId,
    status = "pending",
    platform = null,
    accreditationType = null,
    partnerId = null,
  ) {
    void userId;
    void courseId;
    void status;
    void platform;
    void accreditationType;
    void partnerId;
    throw new Error(
      "Direct course enrollment is disabled. Use the server-owned free, payment, invite, or assignment workflow.",
    );
  },

  /**
   * Normalizes backend field names to a consistent UI-friendly format.
   * Handles legacy field names and provides defaults.
   */
  normalizeEnrollment(e) {
    if (!e) return null;
    return {
      ...e,
      status: e.enrollment_status || e.status || "active",
      enrolled_at: e.enrolled_at || e.enrollment_date || e.created_at,
      completed_at: e.completed_at || e.completion_date || null,
      progress_percentage: e.progress_percentage || 0,
      course: e.course || {},
    };
  },

  /**
   * Calculates summary statistics from a list of enrollments.
   * Centralizes the math used by Dashboards.
   */
  calculateStats(enrollments) {
    if (!enrollments || enrollments.length === 0) {
      return {
        total: 0,
        completed: 0,
        inProgress: 0,
        avgProgress: 0,
        totalStudyHours: 0,
      };
    }

    const total = enrollments.length;
    const completed = enrollments.filter(
      (e) => e.status === "completed" || e.progress_percentage === 100,
    ).length;
    const inProgress = enrollments.filter(
      (e) =>
        e.status === "active" &&
        e.progress_percentage > 0 &&
        e.progress_percentage < 100,
    ).length;

    const avgProgress = Math.round(
      enrollments.reduce((acc, e) => acc + (e.progress_percentage || 0), 0) /
        total,
    );

    const totalStudyHours = enrollments.reduce((acc, e) => {
      const duration = e.course?.duration_hours || 0;
      if (e.status === "completed") return acc + duration;
      return acc + Math.round((duration * (e.progress_percentage || 0)) / 100);
    }, 0);

    return {
      total,
      completed,
      inProgress,
      avgProgress,
      totalStudyHours,
    };
  },

  /**
   * Get detailed enrollment data for a student, including lesson counts and study time.
   * Used primarily by Admin dashboards.
   */
  async getStudentEnrollmentDetails(studentId) {
    if (!studentId) return [];

    try {
      const excluded = await getAnalyticsTransactionExclusionIds();
      const [enrollmentData, pendingData] = await Promise.all([
        fetchAllRows(() => excludeAnalyticsTransactions(supabase
          .from("course_enrollments")
          .select(`
            id, course_id, enrolled_at, completed_at, progress_percentage,
            enrollment_status, accreditation_type, delivery_mode, certifier_public_id,
            course:courses(id, title, thumbnail_url, lessons(id, type, is_published, delivery_mode))
          `)
          .eq("user_id", studentId)
          .in("enrollment_status", ["active", "completed"])
          .order("enrolled_at", { ascending: false }), excluded.courseEnrollmentIds)),
        fetchAllRows(() => excludeAnalyticsTransactions(supabase
          .from("partner_transactions")
          .select("id, created_at, course:courses(id, title, thumbnail_url)")
          .eq("user_id", studentId).eq("status", "manual_pending")
          .order("created_at", { ascending: false }), excluded.partnerTransactionIds)),
      ]);
      const enrollmentIds = enrollmentData.map((e) => e.id);
      const allProgress = [];
      for (let offset = 0; offset < enrollmentIds.length; offset += 100) {
        allProgress.push(...await fetchAllRows(() => supabase.from("lesson_progress")
          .select("id, enrollment_id, lesson_id, is_completed, last_accessed_at, time_spent_minutes, scorm_data")
          .in("enrollment_id", enrollmentIds.slice(offset, offset + 100))));
      }

      // Group progress by enrollment_id in memory
      const progressByEnrollment = new Map();
      allProgress.forEach((p) => {
        const progressItems = progressByEnrollment.get(p.enrollment_id) || [];
        if (!progressByEnrollment.has(p.enrollment_id)) {
          progressByEnrollment.set(p.enrollment_id, progressItems);
        }
        progressItems.push(p);
      });

      // 3. Enhance enrollments using pre-fetched data (no per-item queries)
      const enrolledDetails = (enrollmentData || []).map((enrollment) => {
        const lessons = enrollment.course?.lessons || [];
        const countableLessons = getCountableEnrollmentLessons(
          lessons,
          enrollment,
        );
        const countableLessonIds = new Set(
          countableLessons.map((lesson) => lesson.id),
        );
        const totalLessons = countableLessons.length;
        const lessonStats = progressByEnrollment.get(enrollment.id) || [];

        const completedLessons = lessonStats.filter(
          (p) => p.is_completed && countableLessonIds.has(p.lesson_id),
        ).length;
        const totalStudyMinutes = lessonStats.reduce(
          (sum, p) => sum + (p.time_spent_minutes || 0),
          0,
        );

        const lastActive =
          lessonStats.length > 0
            ? lessonStats.reduce((latest, current) => {
                const currentAt = new Date(current.last_accessed_at);
                return currentAt > new Date(latest)
                  ? current.last_accessed_at
                  : latest;
              }, enrollment.enrolled_at)
            : enrollment.enrolled_at;

        // Aggregate quiz scores across all quiz-type lessons in the course
        const quizLessonIds = new Set(
          lessons.filter((l) => l.type === "quiz").map((l) => l.id),
        );
        let quizEarned = 0;
        let quizTotal = 0;
        let quizCount = 0;
        lessonStats.forEach((p) => {
          if (!quizLessonIds.has(p.lesson_id)) return;
          const d = p.scorm_data;
          if (!d || typeof d.quiz_total !== "number") return;
          quizEarned += Number(d.quiz_score || 0);
          quizTotal += Number(d.quiz_total || 0);
          quizCount += 1;
        });
        const quizSummary =
          quizCount > 0 && quizTotal > 0
            ? {
                earned: quizEarned,
                total: quizTotal,
                percentage: Math.round((quizEarned / quizTotal) * 100),
                count: quizCount,
              }
            : null;

        return {
          ...this.normalizeEnrollment(enrollment),
          courseTitle: enrollment.course?.title || "Unknown Course",
          thumbnail: enrollment.course?.thumbnail_url,
          totalLessons,
          completedLessons,
          lastActive,
          studyTimeMinutes: totalStudyMinutes,
          hasCertificate: !!enrollment.certifier_public_id,
          quizSummary,
        };
      });

      // Surface pending partner-approval transactions only when there is no
      // matching real enrollment for the same course — protects against a
      // stale manual_pending row showing up alongside its already-active
      // enrollment if a future workflow gap leaves one orphaned.
      const enrolledCourseIds = new Set(
        (enrollmentData || []).map((e) => e.course?.id).filter(Boolean),
      );
      const pendingDetails = pendingData
        .filter((pt) => pt.course?.id && !enrolledCourseIds.has(pt.course.id))
        .map((pt) => ({
          ...this.normalizeEnrollment({
            id: `pt_${pt.id}`,
            enrolled_at: pt.created_at,
            progress_percentage: 0,
            enrollment_status: "pending_partner_approval",
            course: pt.course,
          }),
          courseTitle: pt.course?.title || "Unknown Course",
          thumbnail: pt.course?.thumbnail_url,
          totalLessons: 0,
          completedLessons: 0,
          lastActive: null,
          studyTimeMinutes: 0,
          hasCertificate: false,
          quizSummary: null,
        }));

      return [...enrolledDetails, ...pendingDetails];
    } catch (error) {
      console.error("Error in getStudentEnrollmentDetails:", error);
      throw error;
    }
  },

  /**
   * Get formatted analytics data for a user.
   */
  async getAnalyticsData(userId) {
    if (!userId) return null;
    try {
      // 1. Get detailed progress data
      const { data: progressData, error: progressError } = await supabase
        .from("user_progress")
        .select(
          `
          *,
          lessons (
            title,
            course_id,
            courses (
              title,
              iso_standard,
              category,
              duration_hours
            )
          ),
          enrollment_id,
          course_enrollments (
            enrollment_status,
            progress_percentage
          )
        `,
        )
        .eq("user_id", userId);

      if (progressError) throw progressError;

      // 2. Get enrollment data
      const { data: enrollmentData, error: enrollmentError } = await supabase
        .from("course_enrollments")
        .select(
          `
          *,
          course:courses (
            title,
            iso_standard,
            category,
            duration_hours
          )
        `,
        )
        .eq("user_id", userId);

      if (enrollmentError) throw enrollmentError;

      return {
        progress: progressData || [],
        enrollments: (enrollmentData || []).map((e) =>
          this.normalizeEnrollment(e),
        ),
      };
    } catch (error) {
      console.error("Error in getAnalyticsData:", error);
      throw error;
    }
  },

  /**
   * Get recent activity feed for a user.
   */
  async getRecentActivity(userId, limit = 8) {
    if (!userId) return [];
    try {
      const { data: progressData, error: progressError } = await supabase
        .from("user_progress")
        .select(
          `
          *,
          lessons (
            title,
            lesson_type,
            course_id,
            courses (
              title,
              iso_standard
            )
          )
        `,
        )
        .eq("user_id", userId)
        .order("last_accessed_at", { ascending: false })
        .limit(5);

      if (progressError) throw progressError;

      const currentPlatform = getCurrentPlatform();
      const { data: enrollmentData, error: enrollmentError } = await supabase
        .from("course_enrollments")
        .select(
          `
          *,
          course:courses (
            title,
            iso_standard
          )
        `,
        )
        .eq("user_id", userId)
        .eq("platform", currentPlatform)
        .order("enrolled_at", { ascending: false })
        .limit(3);

      if (enrollmentError) throw enrollmentError;

      const activities = [
        ...(progressData || []).map((p) => ({
          id: p.id,
          type: "lesson_progress",
          title: `Studied: ${p.lessons?.title}`,
          description: `${p.lessons?.courses?.title || p.lessons?.course?.title} - ${p.lessons?.courses?.iso_standard || p.lessons?.course?.iso_standard}`,
          timestamp: p.last_accessed_at,
          status: p.completion_status,
          icon:
            p.lessons?.type === "video"
              ? "Play"
              : p.lessons?.type === "quiz"
                ? "HelpCircle"
                : "BookOpen",
        })),
        ...(enrollmentData || []).map((e) => ({
          id: e.id,
          type: "course_enrollment",
          title: `Enrolled in: ${e.course?.title || e.courses?.title}`,
          description: `Started learning ${e.course?.iso_standard || e.courses?.iso_standard}`,
          timestamp: e.enrolled_at,
          status: e.enrollment_status,
          icon: "BookOpen",
        })),
      ];

      return activities
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, limit);
    } catch (error) {
      console.error("Error in getRecentActivity:", error);
      throw error;
    }
  },

  /**
   * Get all enrollments with user profiles and course info.
   * Used by Admin dashboards.
   *
   * Also surfaces pending partner transactions (status='manual_pending')
   * as synthetic enrollments so admins can see students who have paid
   * through a partner's external-payment flow but are not yet enrolled.
   */
  async getAllEnrollmentsWithProfiles(options = {}) {
    const rows = await analyticsService.getReportingEnrollments(options);
    return rows.map((row) => this.normalizeEnrollment(row));
  },
};
